import { GlowGetApi, PaginatedResponse } from "@luma-team/shared";
import { Key } from "swr";
import useSWRInfinite, {
  SWRInfiniteConfiguration,
  SWRInfiniteKeyLoader,
  SWRInfiniteResponse,
  unstable_serialize,
} from "swr/infinite";
import { useCallback } from "react";
import { ZmClient } from "@utils/ZmClient";

/**
 * This allows us to make paginated request
 * - Initially we make one request with a limit L
 * - That query returns up to L results and a next_cursor if there is more data
 * - We can request more data by calling the loadMore() function
 *
 * Notes:
 * - When you update the sort, this updates the loading state and page size
 */
export const useZmClientInfinite = <
  Path extends keyof GlowGetApi,
  Entry extends { api_id: string },
  Request extends GlowGetApi[Path]["request"],
  Response extends GlowGetApi[Path]["response"] & PaginatedResponse<Entry>
>({
  path,
  pause,
  args,
  swrConfig,
  pageSize = 100,
}: {
  path: Path;
  pause?: boolean;
  args?: Request;
  swrConfig?: SWRInfiniteConfiguration<Response>;
  pageSize?: number;
}): Pick<SWRInfiniteResponse<Response, Error>, "error" | "mutate"> & {
  data: Response["entries"];
  hasMore: boolean;
  dataInPages: Response[] | undefined;
  loadMore: () => void;
  resetNumPages: () => void;
} => {
  const keyLoader = createZmClientInfiniteKeyLoader({
    path,
    pause,
    args,
  });

  const {
    data: dataInPages,
    error,
    mutate,
    setSize,
  } = useSWRInfinite<Response>(
    keyLoader,

    // First three elements of the key are just userApiId, path & args,
    // used for SWR caching/memoization.  The only element we're interested in
    // for pagination is the next cursor.
    ([_, _path, _jsonArgs, pagination_cursor]) =>
      ZmClient.get<Path, Request, Response>(path, {
        ...args,
        pagination_cursor,
        pagination_limit: pageSize,
      } as any),
    { persistSize: false, ...swrConfig }
  );

  const hasMore = Boolean(
    dataInPages && dataInPages[dataInPages.length - 1].has_more
  );

  const loadMore = useCallback(() => {
    setSize((size) => size + 1);
  }, [setSize]);

  const data = dataInPages && dataInPages.flatMap(({ entries }) => entries);

  const resetNumPages = useCallback(() => {
    setSize(1);
  }, [setSize]);

  return {
    data,
    error,
    mutate,
    hasMore,
    loadMore,
    dataInPages,
    resetNumPages,
  } as any;
};

/**
 * Creates an `swr` key loader - a function that
 * generates a cache key for given arguments.
 */
export const createZmClientInfiniteKeyLoader = ({
  path,
  pause,
  args,
}: {
  path: string;
  pause?: boolean;
  args?: any;
}): SWRInfiniteKeyLoader => {
  const userCacheKeyComponent = "anonymous";

  return (_pageIndex, prevData) => {
    if (pause) {
      return null;
    }

    if (!prevData) {
      return [userCacheKeyComponent, path, JSON.stringify(args)];
    }

    return [
      userCacheKeyComponent,
      path,
      JSON.stringify(args),
      prevData.next_cursor,
    ];
  };
};

/**
 * For a given key loader creates a cache key of
 * the infinite connection cache (in opposition to a single page
 * of data).
 */
export const createZmClientInfiniteCacheKey = (
  keyLoader: SWRInfiniteKeyLoader
): Key => {
  return unstable_serialize(keyLoader);
};
